/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs\AdvancedTab.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions module.
 ────────────────────────────────────────────────────────────────*/

import React, { useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { SecretInput } from '../../../components/ui/SecretField';
import { FormBox } from '../../../components/ui/FormBox';
import MohSelect from '../../../components/ui/MohSelect';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { Settings } from 'lucide-react';

interface AdvancedTabProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;
  errors: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: string, value: any) => void;
  aiAgents?: Array<{ id: number; name: string; description?: string }>;
  aiTranslatorProfiles?: Array<{ id: number; name: string; description?: string }>;
}

export function AdvancedTab({
  formData,
  errors,
  onChange,
  aiAgents = [],
  aiTranslatorProfiles = [],
}: AdvancedTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  // Auto-select default Chatbot if none is selected
  useEffect(() => {
    if (formData.chatbotEnabled && !formData.chatbotAgentId && aiAgents.length > 0) {
      const defaultAgent = aiAgents.find((a: any) => a.isDefaultChatbot);
      if (defaultAgent) {
        onChange('chatbotAgentId', defaultAgent.id);
      }
    }
  }, [formData.chatbotEnabled, formData.chatbotAgentId, aiAgents, onChange]);

  return (
    <div className="space-y-4">
      {/* Advanced Options */}
      <FormBox
        title={t('extensions.advanced.title')}
        icon={<Settings className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          {/* Ring Timeout */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.callTimeout.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.call_timeout')}` : t('extensions.advanced.callTimeout.tooltip')}
            title={t('extensions.advanced.callTimeout.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="callTimeout" className="text-left">
              {t('extensions.advanced.callTimeout.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="callTimeout"
            type="number"
            value={formData.callTimeout || '30'}
            onChange={(e) => onChange('callTimeout', parseInt(e.target.value) || 30)}
            placeholder="30"
            min="15"
            max="120"
            error={errors.callTimeout}
          />

          {/* Max Concurrent Calls */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.maxCalls.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.max_calls')}` : t('extensions.advanced.maxCalls.tooltip')}
            title={t('extensions.advanced.maxCalls.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="maxCalls" className="text-left">
              {t('extensions.advanced.maxCalls.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="maxCalls"
            type="number"
            value={formData.maxCalls || '0'}
            onChange={(e) => onChange('maxCalls', parseInt(e.target.value) || 0)}
            placeholder="0"
            min="0"
            error={errors.maxCalls}
          />

          {/* Call Waiting */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.callWaiting.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.call_waiting')}` : t('extensions.advanced.callWaiting.tooltip')}
            title={t('extensions.advanced.callWaiting.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="callWaiting" className="text-left">
              {t('extensions.advanced.callWaiting.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="callWaiting"
            value={formData.callWaiting ?? true}
            onChange={(value) => onChange('callWaiting', value)}
          />

          {/* Reject Anonymous Calls */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.rejectAnonymous.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.reject_anonymous')}` : t('extensions.advanced.rejectAnonymous.tooltip')}
            title={t('extensions.advanced.rejectAnonymous.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="rejectAnonymous" className="text-left">
              {t('extensions.advanced.rejectAnonymous.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="rejectAnonymous"
            value={formData.rejectAnonymous ?? false}
            onChange={(value) => onChange('rejectAnonymous', value)}
          />

          {/* Auto Answer */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.autoAnswer.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.auto_answer')}` : t('extensions.advanced.autoAnswer.tooltip')}
            title={t('extensions.advanced.autoAnswer.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="autoAnswer" className="text-left">
              {t('extensions.advanced.autoAnswer.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="autoAnswer"
            value={formData.autoAnswer ?? false}
            onChange={(value) => onChange('autoAnswer', value)}
          />

          {/* Music on Hold Class */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.holdMusic.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.music_on_hold')}` : t('extensions.advanced.holdMusic.tooltip')}
            title={t('extensions.advanced.holdMusic.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="holdMusic" className="text-left">
              {t('extensions.advanced.holdMusic.label')}
            </Label>
          </SimpleTooltip>
          <MohSelect
            value={formData.holdMusic || 'default'}
            onChange={(value) => onChange('holdMusic', value)}
            disabled={false}
          />

          {/* Caller ID Privacy */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.privacy.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.privacy')}` : t('extensions.advanced.privacy.tooltip')}
            title={t('extensions.advanced.privacy.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="privacy" className="text-left">
              {t('extensions.advanced.privacy.label')}
            </Label>
          </SimpleTooltip>
          <select
            id="privacy"
            value={formData.privacy || 'none'}
            onChange={(e) => onChange('privacy', e.target.value)}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="none">None</option>
            <option value="full">Full</option>
            <option value="name">Name</option>
            <option value="number">Number</option>
          </select>

          {/* Call Screening */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.callScreen.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.call_screen')}` : t('extensions.advanced.callScreen.tooltip')}
            title={t('extensions.advanced.callScreen.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="callScreen" className="text-left">
              {t('extensions.advanced.callScreen.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="callScreen"
            value={formData.callScreen ?? false}
            onChange={(value) => onChange('callScreen', value)}
          />

          {/* Presence ID */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.presenceId.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.presence_id')}` : t('extensions.advanced.presenceId.tooltip')}
            title={t('extensions.advanced.presenceId.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="presenceId" className="text-left">
              {t('extensions.advanced.presenceId.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="presenceId"
            value={formData.presenceId || ''}
            onChange={(e) => onChange('presenceId', e.target.value)}
            placeholder="${user}@${domain_name}"
            error={errors.presenceId}
          />

          {/* Ringtone */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.ringtone.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.ringtone')}` : t('extensions.advanced.ringtone.tooltip')}
            title={t('extensions.advanced.ringtone.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="ringtone" className="text-left">
              {t('extensions.advanced.ringtone.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="ringtone"
            value={formData.ringtone || ''}
            onChange={(e) => onChange('ringtone', e.target.value)}
            placeholder=""
            error={errors.ringtone}
          />


          {/* PINless Dialing */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.pinless.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.pinless')}` : t('extensions.advanced.pinless.tooltip')}
            title={t('extensions.advanced.pinless.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="pinless" className="text-left">
              {t('extensions.advanced.pinless.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="pinless"
            value={formData.pinless ?? false}
            onChange={(value) => onChange('pinless', value)}
          />

          {/* Dynamic Routing */}
          <SimpleTooltip
            content={isDebugModeActive ? `${t('extensions.advanced.dynamicRouting.tooltip')}\n\n${t('extensions.debug.field')}: ${t('extensions.debug.dynamic_routing')}` : t('extensions.advanced.dynamicRouting.tooltip')}
            title={t('extensions.advanced.dynamicRouting.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="dynamicRoutingEnabled" className="text-left">
              {t('extensions.advanced.dynamicRouting.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="dynamicRoutingEnabled"
            value={formData.dynamicRoutingEnabled ?? false}
            onChange={(value) => onChange('dynamicRoutingEnabled', value)}
          />
          {/* Simultaneous Translation Enabled */}
          <SimpleTooltip
            content="Enable AI Simultaneous Translation for calls involving this extension"
            title="Enable Simultaneous Translation"
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="simultaneousTranslationEnabled" className="text-left">
              Enable Simultaneous Translation
            </Label>
          </SimpleTooltip>
          <Toggle
            id="simultaneousTranslationEnabled"
            value={formData.simultaneousTranslationEnabled ?? false}
            onChange={(value) => onChange('simultaneousTranslationEnabled', value)}
          />

          {/* AI Translator Profile */}
          {formData.simultaneousTranslationEnabled && (
            <>
              <SimpleTooltip
                content="Select the AI Translator Profile that defines the target language, voice, and AI properties."
                title="Translator Profile"
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="aiTranslatorProfileId" className="text-left">
                  Translator Profile
                </Label>
              </SimpleTooltip>
              <SearchableSelect
                id="aiTranslatorProfileId"
                value={formData.aiTranslatorProfileId ? String(formData.aiTranslatorProfileId) : null}
                onChange={(val) => onChange('aiTranslatorProfileId', val ? parseInt(val, 10) : null)}
                options={aiTranslatorProfiles.map((profile: any) => ({ value: String(profile.id), label: profile.name || `Profile ${profile.id}` }))}
                placeholder="Select Translator Profile..."
                searchPlaceholder="Search profiles..."
              />
            </>
          )}
        </div>
      </FormBox>

      {/* Portal Access */}
      <FormBox
        title={t('extensions.portal.title')}
        icon={<Settings className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">
          {/* Enable Portal Access */}
          <SimpleTooltip
            content={t('extensions.portal.enabled.tooltip')}
            title={t('extensions.portal.enabled.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="portalEnabled" className="text-left">
              {t('extensions.portal.enabled.label')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="portalEnabled"
            value={formData.portalEnabled ?? false}
            onChange={(value) => onChange('portalEnabled', value)}
          />

          {/* Portal User */}
          <SimpleTooltip
            content={t('extensions.portal.user.tooltip')}
            title={t('extensions.portal.user.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="portalUser" className="text-left">
              {t('extensions.portal.user.label')}
            </Label>
          </SimpleTooltip>
          <Input
            id="portalUser"
            value={formData.portalUser || ''}
            onChange={(e) => onChange('portalUser', e.target.value)}
            disabled={!formData.portalEnabled}
            error={errors.portalUser}
          />

          {/* Portal Password */}
          <SimpleTooltip
            content={t('extensions.portal.password.tooltip')}
            title={t('extensions.portal.password.label')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="portalPassword" className="text-left">
              {t('extensions.portal.password.label')}
            </Label>
          </SimpleTooltip>
          <SecretInput
            id="portalPassword"
            value={formData.portalPassword || ''}
            onChange={(v) => onChange('portalPassword', v)}
            placeholder="••••••••"
            disabled={!formData.portalEnabled}
            error={errors.portalPassword}
          />

          {/* Softphone Enabled */}
          <SimpleTooltip
            content={t('extensions.portal.softphone.tooltip') || 'Enable WebRTC softphone in the portal for this extension'}
            title={t('extensions.portal.softphone.label') || 'Softphone'}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="softphoneEnabled" className="text-left">
              {t('extensions.portal.softphone.label') || 'Softphone'}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="softphoneEnabled"
            value={formData.softphoneEnabled ?? false}
            onChange={(value) => onChange('softphoneEnabled', value)}
            disabled={!formData.portalEnabled}
          />

          {/* Enable Chatbot */}
          <SimpleTooltip
            content={t('extensions.advanced.chatbot.enabled.tooltip', 'Enable AI chatbot for this extension in the Portal')}
            title={t('extensions.advanced.chatbot.enabled.label', 'Enable Chatbot')}
            position="top"
            maxWidth="300px"
          >
            <Label htmlFor="chatbotEnabled" className="text-left">
              {t('extensions.advanced.chatbot.enabled.label', 'Enable Chatbot')}
            </Label>
          </SimpleTooltip>
          <Toggle
            id="chatbotEnabled"
            value={formData.chatbotEnabled ?? false}
            onChange={(value) => onChange('chatbotEnabled', value)}
            disabled={!formData.portalEnabled}
          />

          {/* Chatbot Agent Selection */}
          {formData.chatbotEnabled && (
            <>
              <SimpleTooltip
                content={t('extensions.advanced.chatbot.agent.tooltip', 'Select the specific AI Agent personality to use')}
                title={t('extensions.advanced.chatbot.agent.label', 'Agent Personality')}
                position="top"
                maxWidth="300px"
              >
                <Label htmlFor="chatbotAgentId" className="text-left">
                  {t('extensions.advanced.chatbot.agent.label', 'Agent Personality')}
                </Label>
              </SimpleTooltip>
              <SearchableSelect
                id="chatbotAgentId"
                value={formData.chatbotAgentId ? String(formData.chatbotAgentId) : null}
                onChange={(val) => onChange('chatbotAgentId', val ? parseInt(val, 10) : null)}
                options={aiAgents.map((agent: any) => ({ value: String(agent.id), label: agent.name || '' }))}
                placeholder={t('common.select', 'Select...')}
                searchPlaceholder={t('common.search', 'Search...')}
                disabled={!formData.portalEnabled}
              />
            </>
          )}
        </div>
      </FormBox>

    </div>
  );
}
